import type { Map as MaplibreMap } from 'maplibre-gl'
import MapService from '@/services/map/MapService'
import { OsmClusterService } from '@/services/map/OsmClusterService'

const HEATMAP_LAYER_SUFFIX = '--hm'

/** Default circle-opacity used by OsmClusterService for unclustered points. */
const CLUSTER_POINTS_DEFAULT_OPACITY = 0.9

const UNCLUSTERED_POINT_FILTER: ['!', ['has', 'point_count']] = ['!', ['has', 'point_count']]

/**
 * Heatmap visualization for OSM point layers that use OsmClusterService.
 *
 * Transition:
 *   - zoom 0–7   : heatmap at full opacity, individual circles invisible
 *   - zoom 7–22  : heatmap fades out, individual circles fade in
 *   - zoom 22+   : heatmap gone (maxzoom:22), individual circles fully visible
 *
 * Cluster bubble layers (circles + count) are hidden for the entire heatmap session
 * because they are not meaningful alongside a density visualization.
 */
export class OsmHeatmapService {
  private static readonly activeHeatmapStyleIds = new Set<string>()

  static heatmapLayerId(styleId: string): string {
    return `${styleId}${HEATMAP_LAYER_SUFFIX}`
  }

  static enable(map: MaplibreMap, styleId: string): void {
    const clusterSourceId = OsmClusterService.clusterSourceId(styleId)
    if (!map.getSource(clusterSourceId)) return

    // Hide cluster bubble layers (circles + count) for the duration of heatmap mode.
    const circlesId = OsmClusterService.clusterCirclesLayerId(styleId)
    const countId = OsmClusterService.clusterCountLayerId(styleId)
    for (const layerId of [circlesId, countId]) {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', 'none')
      }
    }

    // Individual unclustered points: fade in from zoom 7→22 in sync with heatmap fade-out.
    const pointsId = OsmClusterService.clusterPointsLayerId(styleId)
    if (map.getLayer(pointsId)) {
      const layer = map.getLayer(pointsId)
      if (layer?.type === 'circle') {
        map.setPaintProperty(pointsId, 'circle-opacity', [
          'interpolate', ['linear'], ['zoom'],
          7, 0,
          22, CLUSTER_POINTS_DEFAULT_OPACITY,
        ])
        map.setPaintProperty(pointsId, 'circle-stroke-opacity', [
          'interpolate', ['linear'], ['zoom'],
          7, 0,
          22, 1,
        ])
      } else if (layer?.type === 'symbol') {
        map.setPaintProperty(pointsId, 'icon-opacity', [
          'interpolate', ['linear'], ['zoom'],
          7, 0,
          22, 1,
        ])
        map.setPaintProperty(pointsId, 'text-opacity', [
          'interpolate', ['linear'], ['zoom'],
          7, 0,
          22, 1,
        ])
      }
    }

    const beforeId = MapService.getFirstIndicatorLayerId(map) ?? undefined
    const fondMeta = { isPersistent: true, layerGroup: MapService.LAYER_GROUP.FOND }

    const heatmapId = this.heatmapLayerId(styleId)
    if (!map.getLayer(heatmapId)) {
      map.addLayer(
        {
          id: heatmapId,
          type: 'heatmap',
          source: clusterSourceId,
          maxzoom: 22,
          filter: UNCLUSTERED_POINT_FILTER,
          paint: {
            // All OSM points carry equal weight — density emerges from overlap.
            'heatmap-weight': 1,
            // Stronger intensity at mid zoom (e.g. ~1:46k) so nearby points merge visually.
            'heatmap-intensity': [
              'interpolate', ['linear'], ['zoom'],
              0, 1,
              9, 4,
              14, 7,
              22, 8,
            ],
            'heatmap-color': [
              'interpolate',
              ['linear'],
              ['heatmap-density'],
              0,   'rgba(33,102,172,0)',
              0.2, 'rgb(103,169,207)',
              0.4, 'rgb(209,229,240)',
              0.6, 'rgb(253,219,199)',
              0.8, 'rgb(239,138,98)',
              1,   'rgb(178,24,43)',
            ],
            'heatmap-radius': [
              'interpolate', ['linear'], ['zoom'],
              0, 45,
              9, 95,
              14, 125,
              22, 145,
            ],
            // Fades out between zoom 7 and 22, mirroring the circle fade-in.
            'heatmap-opacity': [
              'interpolate', ['linear'], ['zoom'],
              7, 1,
              22, 0,
            ],
          },
          metadata: fondMeta,
        },
        beforeId,
      )
    } else {
      map.setLayoutProperty(heatmapId, 'visibility', 'visible')
    }

    this.activeHeatmapStyleIds.add(styleId)
  }

  static disable(map: MaplibreMap, styleId: string): void {
    const heatmapId = this.heatmapLayerId(styleId)
    if (map.getLayer(heatmapId)) {
      map.removeLayer(heatmapId)
    }

    // Restore cluster bubble layers.
    const circlesId = OsmClusterService.clusterCirclesLayerId(styleId)
    const countId = OsmClusterService.clusterCountLayerId(styleId)
    for (const layerId of [circlesId, countId]) {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', 'visible')
      }
    }

    // Restore original constant opacity on unclustered points.
    const pointsId = OsmClusterService.clusterPointsLayerId(styleId)
    if (map.getLayer(pointsId)) {
      const layer = map.getLayer(pointsId)
      if (layer?.type === 'circle') {
        map.setPaintProperty(pointsId, 'circle-opacity', CLUSTER_POINTS_DEFAULT_OPACITY)
        map.setPaintProperty(pointsId, 'circle-stroke-opacity', 1)
      } else if (layer?.type === 'symbol') {
        map.setPaintProperty(pointsId, 'icon-opacity', 1)
        map.setPaintProperty(pointsId, 'text-opacity', 1)
      }
    }

    this.activeHeatmapStyleIds.delete(styleId)
  }

  /** Alias for {@link disable} — used when tearing down before cluster removal. */
  static remove(map: MaplibreMap, styleId: string): void {
    this.disable(map, styleId)
  }

  static teardownAll(map: MaplibreMap): void {
    for (const styleId of [...this.activeHeatmapStyleIds]) {
      this.disable(map, styleId)
    }
  }
}
